import React, { createContext, useContext, useEffect, useState } from "react";
import { initLiff, isLiffConfigError } from "@/config/liff";
import { logger } from "@/utils/helper";
import { reportError } from "@/utils/helper/error-reporter";

// liff.init loads a script from LINE's CDN on every call and rejects the whole
// init if that one load fails ("Unable to load client features."). A flaky
// phone connection is the usual reason, so give it one more go after a short
// pause before declaring LINE features dead for the session. initLiff never
// caches a rejection, so the second call is a real second attempt.
const LIFF_RETRY_DELAY_MS = 1_500;
// Reports are held this long: a document being replaced at boot (LINE
// re-opening the LIFF, a hub tap) never reaches the timer, so only failures on
// a page that stayed alive are reported.
const LIFF_REPORT_DEFER_MS = 2_000;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// `onRetry` fires right before the second attempt, so the caller can report
// whether a retry actually happened — the catch below also covers liff.ready,
// getProfile() and liff.login(), which never retry.
const initLiffWithOneRetry = async (
  onRetry: () => void,
): Promise<Awaited<ReturnType<typeof initLiff>>> => {
  try {
    return await initLiff();
  } catch (firstError) {
    // No LIFF id on this device: the second attempt would fail identically.
    if (isLiffConfigError(firstError)) throw firstError;
    logger.warn("LIFF init failed once, retrying", firstError);
    onRetry();
    await wait(LIFF_RETRY_DELAY_MS);
    return initLiff();
  }
};

const liffErrorCode = (err: unknown): string | undefined => {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : undefined;
};

export type LiffStatus = "pending" | "success" | "failed_init" | "skipped";

const LiffContext = createContext<LiffStatus>("pending");

// Build the LINE re-login redirect URI so the member returns to the screen they
// tapped instead of always landing on the default page. CallbackPage reads the
// `next` param and rebuilds the destination as `/${next}`, so we pass the
// current path (with its query) minus the leading slash.
const buildLoginRedirectUri = (): string => {
  try {
    // Derived from the current origin so each brand site redirects to itself
    // (LINE only accepts redirect URIs within the LIFF app's endpoint domain).
    const redirectUrl = new URL("/callback", globalThis.location.origin);
    const { pathname, search } = globalThis.location;
    const next = `${pathname}${search}`.replace(/^\/+/, "");
    // Skip empty roots and the callback route itself to avoid redirect loops.
    if (next && !next.startsWith("callback")) {
      redirectUrl.searchParams.set("next", next);
    }
    return redirectUrl.toString();
  } catch {
    return "/callback";
  }
};

// Diagnostic context for a failed init. Guarded: this runs inside the init
// catch block, and a throwing localStorage (locked-down webview) must not
// skip the setStatus("failed_init") that follows.
const describeLiffEntry = (): { hasLiffState?: boolean; hasStoredLiffId?: boolean } => {
  try {
    return {
      hasLiffState: new URLSearchParams(globalThis.location.search).has("liff.state"),
      hasStoredLiffId: !!globalThis.localStorage.getItem("liffId"),
    };
  } catch {
    return {};
  }
};

export const LiffProvider = ({
  children,
  mode,
}: {
  children: React.ReactNode;
  mode: string;
}) => {
  const [status, setStatus] = useState<LiffStatus>(
    mode === "debug" ? "skipped" : "pending",
  );

  useEffect(() => {
    if (mode === "debug") return;

    let isMounted = true;

    const init = async () => {
      let retried = false;
      try {
        const liff = await initLiffWithOneRetry(() => {
          retried = true;
        });
        await liff.ready;
        if (liff.isLoggedIn()) {
          const profile = await liff.getProfile();
          logger.info("LIFF init success", profile);
          if (isMounted) {
            localStorage.setItem("userId", profile.userId);
            setStatus("success");
          }
        } else {
          liff.login({ redirectUri: buildLoginRedirectUri() });
        }
      } catch (err) {
        logger.error("LIFF init failed", err);
        // Without LIFF the LINE-side features are dead for this session.
        reportError(err, {
          deferMs: LIFF_REPORT_DEFER_MS,
          extra: {
            stage: "liff-init",
            liffCode: liffErrorCode(err),
            retried,
            ...describeLiffEntry(),
          },
        });
        if (isMounted) setStatus("failed_init");
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [mode]);

  return <LiffContext.Provider value={status}>{children}</LiffContext.Provider>;
};

export const useLiffStatus = () => useContext(LiffContext);
